/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { TradeRuleProposeNominated } from 'src/components/TradeRule';
import TradeRuleProposeNominatedPlaceholder from 'src/components/TradeRule/TradeRuleProposeNominated/Placeholder';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import buildTradeRuleProposeNominatedRetryVariables from './helpers/buildTradeRuleProposeNominatedRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'trade_rule.trade_rule_propose_nominated_route.initial_load_error.message',
    defaultMessage: 'An error occurred while loading the trade rule proposal. Please try again.',
  },
});

export const tradeRuleProposeNominatedRouteQuery = graphql`
  query TradeRuleProposeNominatedRoute_Query($uuid: ID!, $now: Timestamp!) {
    property(uuid: $uuid) {
      ...TradeRuleProposeNominated_property
    }
  }
`;

const tradeRuleProposeNominatedRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }),
});

/**
 * Renders `TradeRuleProposeNominated` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} trade rule propose nominated route content.
 */
function TradeRuleProposeNominatedPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(tradeRuleProposeNominatedRouteQuery, queryRef);
  return <TradeRuleProposeNominated {...routeProps} {...queryData} />;
}

TradeRuleProposeNominatedPreloadedQueryRenderer.propTypes = {
  routeProps: tradeRuleProposeNominatedRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `TradeRuleProposeNominated` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * Caller MUST mount this with `key={routeProps.match.params.uuid}`
 * so a uuid change disposes the stale `useQueryLoader` query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} trade rule propose nominated route content.
 */
function TradeRuleProposeNominatedRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    tradeRuleProposeNominatedRouteQuery,
    routeProps,
    {
      logLabel: 'TradeRuleProposeNominatedRoute',
      getRetryVariablesFunc: buildTradeRuleProposeNominatedRetryVariables,
    },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={TradeRuleProposeNominatedPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <TradeRuleProposeNominatedPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <TradeRuleProposeNominated {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

TradeRuleProposeNominatedRouteRenderer.propTypes = {
  routeProps: tradeRuleProposeNominatedRoutePropsShape.isRequired,
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the TradeRuleProposeNominated route state supplied by found-relay.
 * Network errors are handled before pending props so the local error boundary mounts.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} TradeRuleProposeNominated route content or loading state.
 */
export function tradeRuleProposeNominatedRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <TradeRuleProposeNominatedRouteRenderer
        key={routeProps.match.params.uuid}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <TradeRuleProposeNominatedRouteRenderer
      key={props.match.params.uuid}
      routeProps={props}
    />
  );
}

export default TradeRuleProposeNominatedRouteRenderer;
